import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Search, MessageCircle, User, LayoutDashboard, Store, ArrowLeftRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBusiness } from '@/contexts/BusinessContext';
import { useCommunityMember } from '@/contexts/CommunityMemberContext';
import { useNotifications } from '@/hooks/useNotifications';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { hasAdminDashboardAccess } from '@/utils/userUtils';

const BottomNavigationBar: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { isBusinessOwner } = useBusiness();
  const { userRole } = useCommunityMember();
  const { unreadCount: totalUnreadCount } = useNotifications();
  const { t } = useTranslation();
  
  const canAccessDashboard = hasAdminDashboardAccess(userRole, isBusinessOwner);
  
  // Scroll behavior state
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 100; // Start hiding after 100px scroll
      
      // Always show when at top
      if (currentScrollY < scrollThreshold) {
        setIsVisible(true);
      }
      // Hide when scrolling down, show when scrolling up
      else if (currentScrollY > lastScrollY && currentScrollY > scrollThreshold) {
        setIsVisible(false); // Scrolling down
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true); // Scrolling up
      }
      
      setLastScrollY(currentScrollY);
    };

    // Throttle scroll events for better performance
    let timeoutId: NodeJS.Timeout;
    const throttledHandleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 10);
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      clearTimeout(timeoutId);
    };
  }, [lastScrollY]);

  // Reset visibility when route changes
  useEffect(() => {
    setIsVisible(true);
    setLastScrollY(0);
  }, [location.pathname]);

  // Define navigation items based on user role
  const getNavigationItems = () => {
    // Always show these items
    const items = [
      {
        name: t('header.nav.home'),
        path: '/',
        icon: Home,
      },
      {
        name: t('header.nav.services'),
        path: '/services',
        icon: Search,
      },
      {
        name: t('header.nav.request') || 'Request',
        path: '/service-request',
        icon: ArrowLeftRight,
      },
    ];

    if (!user) {
      // Non-authenticated users: just add login
      items.push({
        name: t('header.nav.signin'),
        path: '/auth',
        icon: User,
      });
    } else {
      // Authenticated users: add chat and appropriate profile/admin option
      items.push({
        name: t('header.nav.chats') || 'Chat',
        path: '/conversations',
        icon: MessageCircle,
        badge: totalUnreadCount,
      });

      // Last item based on role - prioritize admin over business owner
      if (canAccessDashboard) {
        items.push({
          name: t('header.nav.dashboard'),
          path: '/admin',
          icon: LayoutDashboard,
        });
      } else if (isBusinessOwner) {
        items.push({
          name: t('header.nav.my_business'),
          path: '/admin/my-business',
          icon: Store,
        });
      } else {
        items.push({
          name: t('header.nav.profile'),
          path: '/profile',
          icon: User,
        });
      }
    }

    return items;
  };

  const navigationItems = getNavigationItems();

  return (
    <nav 
      className={`lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 safe-area-padding-bottom transition-transform duration-300 ease-in-out ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      <div className="flex justify-around items-center h-16 px-1">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 h-full relative py-2 ${
                isActive ? 'text-primary' : 'text-gray-500'
              }`}
            >
              <div className="relative mb-1">
                <Icon className={`w-6 h-6 ${isActive ? 'text-primary' : 'text-gray-500'}`} />
                {item.badge > 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-2 -right-2 h-4 min-w-4 flex items-center justify-center p-0 text-[9px] rounded-full"
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </Badge>
                )}
              </div>
              <span className={`text-xs leading-none ${isActive ? 'font-medium text-primary' : 'text-gray-500'}`}>
                {item.name}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavigationBar;